import { useConversationMembers } from '@/features/conversation';
import { Delete } from '@mui/icons-material';
import { IconButton, ListItemButton, ListItemText, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { memo, useMemo } from 'react';
import { MessageContentType } from '../../models/message-content-type';
import type { PinnedMessage } from '../../models/message-pinned';

interface PinnedMessageItemProps {
  curUserId: string;
  message: PinnedMessage;
  onClick?: (messageId: string) => void;
  onDelete?: (messageId: string, bucketId: string) => void;
  showPinnedInfo?: boolean;
}

const formatPinnedTime = (date: string): string => {
  const pinnedDate = dayjs(date);
  const now = dayjs();

  if (now.diff(pinnedDate, 'day') < 1) {
    return pinnedDate.format('HH:mm');
  }
  if (now.diff(pinnedDate, 'day') < 7) {
    return pinnedDate.format('dddd');
  }
  return pinnedDate.format('DD/MM/YYYY');
};

export const MessageListPinnedItem = memo<PinnedMessageItemProps>(
  ({ message, onClick, curUserId, onDelete, showPinnedInfo = true }) => {
    const { data: members } = useConversationMembers(message.conversationId);

    const [senderName, pinnerName] = useMemo(() => {
      if (!Array.isArray(members)) return ['', ''];
      const sender = members?.find((member) => member.userId === message.senderId)?.user?.fullName || 'Unknown User';
      const pinner = members?.find((member) => member.userId === message.pinnedBy)?.user?.fullName || 'Unknown User';
      return [sender, pinner];
    }, [members, message.senderId]);

    const handleClick = () => {
      onClick?.(message.messageId);
    };

    const getContentPreview = () => {
      switch (message.contentType) {
        case MessageContentType.IMAGE:
          return '📷 Hình ảnh';
        case MessageContentType.COMPOSE:
        case MessageContentType.COMBINE:
          return '📎 Tin nhắn đa phương tiện';
        default:
          return message.content;
      }
    };

    return (
      <ListItemButton onClick={handleClick}>
        <ListItemText
          primary={
            <Stack direction='row' alignItems='center' justifyContent='space-between' mb={0.5}>
              <Typography
                variant='body2'
                sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  flex: 1,
                  mr: 1,
                  textAlign: 'left'
                }}
                noWrap
              >
                {senderName}
              </Typography>
              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                {formatPinnedTime(message.sentAt.toString())}
              </Typography>
            </Stack>
          }
          secondary={
            <Stack>
              <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    mb: showPinnedInfo ? 1 : 0,
                    textAlign: 'left'
                  }}
                >
                  {getContentPreview()}
                </Typography>
                <Stack direction='column' alignItems='center' spacing={1} flexShrink={0}>
                  {curUserId === message.pinnedBy && (
                    <IconButton
                      color='error'
                      size='small'
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(message.messageId, message.bucketId!);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Stack>
              </Stack>
              {showPinnedInfo && (
                <Typography variant='caption' sx={{ color: 'text.secondary', flexShrink: 0 }}>
                  📌 Ghim bởi {pinnerName} • {formatPinnedTime(message.pinnedAt.toString())}
                </Typography>
              )}
            </Stack>
          }
        />
      </ListItemButton>
    );
  }
);
